/* Mobile-only floating action button for CCTNS IIF downloads.
 *
 * Hidden on ≥sm screens (desktop has the IIFCaseStrip + KPI tile inline).
 * On mobile, expands to a 7-form action sheet. If the user's current route
 * contains a known ID pattern (firs/:id, charge-sheets/:id, arrests/:id…),
 * we deep-link directly to the relevant mobile IIF viewer with that ID.
 */
import { useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

interface Form {
  n: 1 | 2 | 3 | 4 | 5 | 6 | 7;
  label: string;
  labelMr: string;
  emoji: string;
}

const FORMS: Form[] = [
  { n: 1, label: "FIR Form",         labelMr: "प्रथम माहिती",   emoji: "📋" },
  { n: 2, label: "Crime Detail",     labelMr: "गुन्हा तपशील",   emoji: "🔍" },
  { n: 3, label: "Arrest Form",      labelMr: "अटक फॉर्म",       emoji: "🚔" },
  { n: 4, label: "Charge Sheet",     labelMr: "आरोपपत्र",        emoji: "📑" },
  { n: 5, label: "Court Disposal",   labelMr: "न्यायालय निकाल",   emoji: "⚖️" },
  { n: 6, label: "Appeal Result",    labelMr: "अपील निकाल",     emoji: "📜" },
  { n: 7, label: "Panchanama",       labelMr: "पंचनामा",         emoji: "📝" },
];

/** Extract a known {form, id} pair from the current URL, if possible. */
function detectContext(pathname: string): { form?: number; id?: string } {
  // FIR-context routes → IIF-1, IIF-2
  let m = pathname.match(/^\/firs\/([0-9a-f-]{36})/i);
  if (m) return { form: 1, id: m[1] };
  m = pathname.match(/^\/iif2\/([0-9a-f-]{36})/i);
  if (m) return { form: 2, id: m[1] };
  // Arrest
  m = pathname.match(/^\/arrests\/([0-9a-f-]{36})/i);
  if (m) return { form: 3, id: m[1] };
  // Charge sheet
  m = pathname.match(/^\/charge-sheets\/([0-9a-f-]{36})/i);
  if (m) return { form: 4, id: m[1] };
  // Court filing
  m = pathname.match(/^\/court\/filings\/([0-9a-f-]{36})/i);
  if (m) return { form: 5, id: m[1] };
  // Appeal
  m = pathname.match(/^\/appeals\/([0-9a-f-]{36})/i);
  if (m) return { form: 6, id: m[1] };
  // Panchanama
  m = pathname.match(/^\/panchanamas\/([0-9a-f-]{36})/i);
  if (m) return { form: 7, id: m[1] };
  return {};
}


export function MobileIIFFAB() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const nav = useNavigate();
  const ctx = useMemo(() => detectContext(pathname), [pathname]);

  // Hide entirely on routes where IIF makes no sense
  if (pathname.startsWith("/login") || pathname.startsWith("/citizen") || pathname === "/kiosk") {
    return null;
  }

  return (
    <>
      {/* FAB — small screens only */}
      <button
        onClick={() => setOpen(true)}
        aria-label="Open IIF download menu"
        className="fixed bottom-4 right-4 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-mhpol-700 text-white shadow-xl ring-4 ring-mhpol-100 transition active:scale-95 sm:hidden"
        title="CCTNS IIF Downloads">
        <span className="text-lg">📄</span>
        {ctx.form && (
          <span className="absolute -top-1 -right-1 rounded-full bg-emerald-500 px-1.5 py-0.5 text-[9px] font-bold text-white">
            IIF-{ctx.form}
          </span>
        )}
      </button>

      {/* Sheet — bottom slide-up */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:hidden" onClick={() => setOpen(false)}>
          <div className="absolute inset-0 bg-black/50" />
          <div className="relative w-full rounded-t-2xl bg-white p-4 shadow-2xl"
               onClick={e => e.stopPropagation()}>
            <div className="mx-auto mb-3 h-1 w-12 rounded-full bg-slate-300" />
            <div className="mb-2 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-wider text-mhpol-700">CCTNS IIF Forms</div>
                <div className="text-[10px] text-slate-500">All 7 render LIVE — no stale data</div>
              </div>
              <button onClick={() => setOpen(false)}
                className="rounded-full bg-slate-100 px-3 py-1 text-xs">Close</button>
            </div>

            {ctx.form && ctx.id && (
              <div className="mb-3 rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-xs">
                <div className="font-semibold text-emerald-900">
                  Quick action for this page
                </div>
                <div className="mt-0.5 text-emerald-800">
                  Tap to open IIF-{ctx.form} for {ctx.id.slice(0, 8)}…
                </div>
                <button onClick={() => { setOpen(false); nav(`/m/iif/${ctx.form}/${ctx.id}`); }}
                  className="mt-2 w-full rounded-lg bg-emerald-700 px-3 py-2 text-sm font-semibold text-white active:bg-emerald-800">
                  Open IIF-{ctx.form} →
                </button>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              {FORMS.map(f => (
                <button key={f.n}
                  onClick={() => { setOpen(false); nav("/m/iif"); }}
                  className="flex flex-col items-start rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-left active:bg-mhpol-50">
                  <div className="flex items-center gap-1.5 text-sm font-semibold text-mhpol-900">
                    <span>{f.emoji}</span>
                    <span>IIF-{f.n}</span>
                  </div>
                  <div className="text-[11px] text-slate-700">{f.label}</div>
                  <div className="font-devanagari text-[10px] text-slate-500">{f.labelMr}</div>
                </button>
              ))}
            </div>

            <div className="mt-3 flex gap-2 text-xs">
              <button onClick={() => { setOpen(false); nav("/m/iif"); }}
                className="flex-1 rounded-lg bg-mhpol-700 px-3 py-2 font-semibold text-white">
                Open IIF Hub
              </button>
              <button onClick={() => { setOpen(false); nav("/iif/drift"); }}
                className="rounded-lg border border-slate-300 bg-white px-3 py-2">
                Drift
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
